import React, { useCallback } from 'react'

import { useConversationId } from 'conversations/hooks/useConversationId'
import { permacloseConversation } from 'conversations/modules/conversation/api'
import { getCurrentConversation } from 'conversations/modules/conversation/selectors'
import {
  addOrgUserToConversation,
  closeAndLeaveConversation,
  removeOrgUserFromConversation,
} from 'conversations/modules/user/api'
import { getCurrentUser } from 'conversations/modules/user/selectors'
import { captureError } from 'conversations/utils/logRocket'
import { hasOnlyMyConvosAccess } from 'conversations/utils/sectionUtils'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { updateConversationStatusAsync } from 'shared/store/actions/conversation'
import { ConversationStatus } from 'shared/typings/conversations/status/ConversationStatus'
import { User } from 'shared/typings/user/User'

import { RootState } from 'typesafe-actions'

const noop = () => null

export default function useConversationActions(selectedConversationId?: number): any {
  const conversationId = useConversationId()
  const currentConversation = useSelector(getCurrentConversation)
  const currentUser = useSelector<RootState, User | undefined>(getCurrentUser)
  const dispatch = useDispatch()
  const history = useHistory()

  const convoIdToUse = selectedConversationId || conversationId

  const joinConversation = useCallback(
    async (joinCallback = noop) => {
      if (currentUser && convoIdToUse) {
        try {
          await addOrgUserToConversation(convoIdToUse, currentUser.id)
          joinCallback()
        } catch (err) {
          captureError(err, 'Add org user to convo failed')
        }
      }
    },
    [convoIdToUse, currentUser]
  )

  const leaveConversation = useCallback(
    async (leaveCallback = noop) => {
      if (convoIdToUse && currentUser) {
        try {
          await removeOrgUserFromConversation(convoIdToUse, currentUser.id)
          if (hasOnlyMyConvosAccess()) {
            history.push('/conversations')
          }
          leaveCallback()
        } catch (err) {
          captureError(err, 'Remove org user from convo failed')
        }
      }
    },
    [convoIdToUse, currentUser, history]
  )

  const closeAndLeave = useCallback(async () => {
    if (currentConversation) {
      try {
        await closeAndLeaveConversation(currentConversation.id)
        history.push('/conversations')
      } catch (err) {
        captureError(err, 'Close and leave convo failed')
      }
    }
  }, [currentConversation, history])

  const updateConversationStatus = useCallback(
    (conversationStatus: ConversationStatus): void => {
      convoIdToUse &&
        dispatch(
          updateConversationStatusAsync.request({
            conversationId: convoIdToUse,
            conversationStatus: conversationStatus,
          })
        )
    },
    [convoIdToUse, dispatch]
  )

  const closeConversation = useCallback(
    (event: React.MouseEvent, closeCallback = noop): void => {
      typeof event !== 'string' && event.stopPropagation()
      updateConversationStatus(ConversationStatus.CLOSED)
      closeCallback()
    },
    [updateConversationStatus]
  )

  const permanentlyCloseConversation = useCallback(
    (closeCallback = noop): void => {
      convoIdToUse && permacloseConversation(convoIdToUse)
      closeCallback()
    },
    [convoIdToUse]
  )

  const openConversation = useCallback(
    (openCallback = noop): void => {
      updateConversationStatus(ConversationStatus.OPEN)
      openCallback()
    },
    [updateConversationStatus]
  )

  return {
    joinConversation,
    leaveConversation,
    closeConversation,
    openConversation,
    closeAndLeave,
    updateConversationStatus,
    permanentlyCloseConversation,
  }
}
